// Story 7-FU-D (ADO-1217), AC4 — hydrate favourites once at authenticated boot.
//
// The favourites store previously only hydrated on the /favourites screen
// (Favourites.tsx), so the per-card heart on /browse and /search showed empty
// until the user visited /favourites. This wires a SINGLE app-wide hydration to
// the authenticated signal, so `favouriteSkus` is populated wherever a
// ProductCard renders on a fresh reload.
//
// It deliberately lives at the composition root (main.tsx), NOT inside a screen
// component or AuthProvider: the original deferral (deferred-work.md:869) skipped
// app-boot hydration to avoid coupling every listing screen's msw tests to the
// favourites endpoint. A root-level store subscription keeps that coupling out —
// component specs never mount this — while a hydration failure is swallowed by
// the store's own `error` flag (hearts stay unfilled, nothing crashes), and the
// /favourites screen's own retry (`hydrate()` again) is unaffected.

import { useAuthStore } from '../auth/useAuthStore';
import { useFavouritesStore } from './useFavouritesStore';

/**
 * Subscribe to the auth store and hydrate favourites exactly ONCE, the first time
 * the session settles into `ready && isAuthenticated`. Idempotent by construction:
 * it fires once then unsubscribes, so it never re-hydrates per auth-store update.
 * Returns an unsubscribe handle (for tests / teardown symmetry). Fires
 * synchronously if the session is already authenticated when wired.
 */
export function startFavouritesHydrationOnAuth(): () => void {
  let fired = false;

  const maybeHydrate = (): void => {
    if (fired) return;
    const { status, isAuthenticated } = useAuthStore.getState();
    if (status !== 'ready' || !isAuthenticated) return;
    fired = true;
    unsubscribe();
    // Fire-and-forget: the store swallows a failure into its `error` flag, so the
    // unhandled-rejection surface stays clean and hearts never blank on failure.
    void useFavouritesStore.getState().hydrate();
  };

  const unsubscribe = useAuthStore.subscribe(maybeHydrate);
  // Cover the race where the bootstrap already reported authenticated before this
  // wiring ran (subscribe only fires on subsequent changes).
  maybeHydrate();
  return unsubscribe;
}
